import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { colors, fontMono, BANDS, MODES, isBandModeBlocked, s } from "../lib/theme";

export default function Organizer() {
  const [clubs, setClubs] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [tab, setTab] = useState("actividades");
  const [error, setError] = useState(null);

  useEffect(() => {
    api.listClubs().then((data) => {
      setClubs(data);
      if (data.length) setSelectedClub(data[0].id);
    }).catch((err) => setError(err.message));
  }, []);

  if (error) return <div style={s.page}><div style={s.errorBox}>{error}</div></div>;
  if (!clubs) return <div style={s.page}><p style={{ color: colors.textDim }}>Cargando…</p></div>;
  if (!clubs.length) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={{ color: colors.textDim, margin: 0 }}>
            No administras ningún club todavía. Pide al superadministrador que te asigne como admin de uno.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div>
          <div style={s.eyebrow}>PANEL DE ORGANIZADOR</div>
          {clubs.length > 1 ? (
            <select
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              style={{ ...s.h1, background: "transparent", color: colors.text, border: "none", cursor: "pointer" }}
            >
              {clubs.map((c) => (
                <option key={c.id} value={c.id} style={{ background: colors.panelRaised, color: colors.text }}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <h1 style={s.h1}>{clubs[0].name}</h1>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: `1px solid ${colors.line}` }}>
        <TabBtn active={tab === "actividades"} onClick={() => setTab("actividades")}>Mis actividades</TabBtn>
        <TabBtn active={tab === "miembros"} onClick={() => setTab("miembros")}>Miembros del club</TabBtn>
      </div>

      {tab === "actividades" ? <ActivitiesTab clubId={selectedClub} /> : <MembersTab clubId={selectedClub} />}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: "transparent", border: "none", borderBottom: `2px solid ${active ? colors.amber : "transparent"}`,
      color: active ? colors.text : colors.textDim, fontFamily: "inherit", fontSize: 14.5, fontWeight: 600,
      padding: "10px 6px", cursor: "pointer",
    }}>
      {children}
    </button>
  );
}

function ActivitiesTab({ clubId }) {
  const [activities, setActivities] = useState(null);
  const [error, setError] = useState(null);
  const [reportMsg, setReportMsg] = useState(null);
  const [qrzWidgetActivity, setQrzWidgetActivity] = useState(null);
  const [formState, setFormState] = useState(null); // null | "new" | activityId (edición)
  const emptyForm = {
    name: "", callsign: "", startsAt: "", endsAt: "", bands: [], modes: [],
    duplicateScope: "band_mode", requireRst: true, requireExchange: false,
    requireLocator: false, clusterPublishEnabled: false, statsReportMode: "none",
    scoringMode: "fixed", pointsFixed: 1, pointsByBand: {}, pointsByBandMode: {},
    qslEnabled: false, qslMode: "per_contact", qslDaysValid: "", exchangeScope: "general", chatRetentionDays: 30,
    isOta: false,
  };
  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [members, setMembers] = useState(null);
  const [operators, setOperators] = useState({});

  const load = () => api.listActivities().then((all) => setActivities(all.filter((a) => a.club_id === clubId))).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [clubId]);
  useEffect(() => { api.listClubMembers(clubId).then(setMembers).catch(() => setMembers([])); }, [clubId]);

  const toggle = (arr, val) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  // Muestra el valor guardado (UTC) tal cual en el selector, en vez de
  // convertirlo a la hora local del navegador — así el organizador siempre
  // ve y escribe la hora en UTC, sin confusiones entre husos horarios.
  const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  };

  // El <input type="datetime-local"> no tiene noción de huso horario — se
  // interpreta el valor tal cual como si ya fuera UTC, añadiendo la "Z".
  const toUtcIso = (localValue) => (localValue ? `${localValue}:00Z` : null);

  const openCreate = () => {
    setForm(emptyForm);
    setFormState("new");
  };

  const openEdit = (a) => {
    setForm({
      name: a.name || "",
      callsign: a.callsign || "",
      startsAt: toDatetimeLocal(a.starts_at),
      endsAt: toDatetimeLocal(a.ends_at),
      bands: a.bands || [],
      modes: a.modes || [],
      duplicateScope: a.duplicate_scope || "band_mode",
      requireRst: a.require_rst !== false,
      requireExchange: !!a.require_exchange,
      requireLocator: !!a.require_locator,
      clusterPublishEnabled: !!a.cluster_publish_enabled,
      statsReportMode: a.stats_report_mode || "none",
      isOta: !!a.is_ota,
      scoringMode: a.scoring_mode || "fixed",
      pointsFixed: a.points_fixed ?? 1,
      pointsByBand: a.points_by_band || {},
      pointsByBandMode: a.points_by_band_mode || {},
      qslEnabled: !!a.qsl_enabled,
      qslMode: a.qsl_mode || "per_contact",
      qslDaysValid: a.qsl_days_valid ?? "",
      exchangeScope: a.exchange_scope || "general",
      chatRetentionDays: a.chat_retention_days ?? 30,
    });
    setFormState(a.id);
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Seguro que quieres eliminar "${a.name}"? Esto borra también su log de QSOs y no se puede deshacer.`)) return;
    await api.deleteActivity(a.id);
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    const missing = [];
    if (!form.name.trim()) missing.push("nombre");
    if (!form.startsAt) missing.push("fecha de inicio");
    if (!form.callsign.trim()) missing.push("indicativo de la actividad");
    if (missing.length) {
      setError(`Faltan campos obligatorios: ${missing.join(", ")}`);
      return;
    }

    try {
      let activityId = formState;
      const payload = {
        ...form,
        startsAt: toUtcIso(form.startsAt),
        endsAt: toUtcIso(form.endsAt),
        pointsFixed: Number(form.pointsFixed) || 0,
        pointsByBand: Object.fromEntries(
          Object.entries(form.pointsByBand).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])
        ),
        pointsByBandMode: Object.fromEntries(
          Object.entries(form.pointsByBandMode).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])
        ),
        qslDaysValid: form.qslDaysValid === "" ? null : Math.min(60, Number(form.qslDaysValid)),
        chatRetentionDays: Number(form.chatRetentionDays) || 30,
      };
      if (formState === "new") {
        const created = await api.createActivity({ ...payload, clubId });
        activityId = created.id;
      } else {
        await api.updateActivity(formState, payload);
      }
      if (logoFile && activityId) {
        await api.uploadActivityLogo(activityId, logoFile);
      }
      setForm(emptyForm);
      setLogoFile(null);
      setFormState(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const loadOperators = (activityId) => {
    api.listOperators(activityId).then((rows) => setOperators((p) => ({ ...p, [activityId]: rows })));
  };

  const toggleExpand = (activityId) => {
    if (expanded === activityId) { setExpanded(null); return; }
    setExpanded(activityId);
    loadOperators(activityId);
  };

  const addOp = async (activityId, userId) => {
    await api.addOperator(activityId, userId, "operador");
    loadOperators(activityId);
  };
  const removeOp = async (activityId, userId) => {
    await api.removeOperator(activityId, userId);
    loadOperators(activityId);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <p style={{ color: colors.textDim, margin: 0, fontSize: 14 }}>Concursos y activaciones organizados por tu equipo.</p>
        <button onClick={openCreate} style={s.btnPrimary}>+ Nueva actividad</button>
      </div>

      {formState && (
        <form onSubmit={submit} style={{ ...s.card, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: colors.textFaint, letterSpacing: "0.06em" }}>
              {formState === "new" ? "NUEVA ACTIVIDAD" : "EDITANDO ACTIVIDAD"}
            </span>
            <button type="button" onClick={() => setFormState(null)} style={{ background: "transparent", border: "none", color: colors.textDim, cursor: "pointer", fontSize: 13 }}>
              Cancelar
            </button>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ flex: "1 1 240px" }}>
              <label style={s.label}>NOMBRE DE LA ACTIVIDAD *</label>
              <input style={s.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div style={{ width: 140 }}>
              <label style={s.label}>INDICATIVO *</label>
              <input
                style={{ ...s.input, textTransform: "uppercase" }}
                value={form.callsign}
                onChange={(e) => setForm({ ...form, callsign: e.target.value.toUpperCase() })}
                placeholder="EA5XXX"
              />
            </div>
            <div>
              <label style={s.label}>INICIO (UTC) *</label>
              <input type="datetime-local" style={s.input} value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
            </div>
            <div>
              <label style={s.label}>FIN (UTC)</label>
              <input type="datetime-local" style={s.input} value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>LOGO (JPG O PNG, OPCIONAL)</label>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              style={{ fontSize: 13, color: colors.textDim }}
            />
          </div>

          <label style={s.label}>BANDAS</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {BANDS.map((b) => (
              <Toggle key={b.id} active={form.bands.includes(b.id)} onClick={() => setForm({ ...form, bands: toggle(form.bands, b.id) })}>{b.label}</Toggle>
            ))}
          </div>

          <label style={s.label}>MODOS</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {MODES.map((m) => (
              <Toggle key={m} active={form.modes.includes(m)} onClick={() => setForm({ ...form, modes: toggle(form.modes, m) })}>{m}</Toggle>
            ))}
          </div>
          {(() => {
            const blockedPairs = form.bands.flatMap((bandId) =>
              form.modes.filter((m) => isBandModeBlocked(bandId, m)).map((m) => `${BANDS.find((b) => b.id === bandId)?.label || bandId} + ${m}`)
            );
            if (!blockedPairs.length) return null;
            return (
              <p style={{ fontSize: 12, color: colors.amber, marginTop: -4, marginBottom: 8 }}>
                ⚠ {blockedPairs.join(", ")} no {blockedPairs.length === 1 ? "está permitido" : "están permitidos"} por el plan de bandas — esas casillas aparecerán bloqueadas en la matriz aunque las marques aquí.
              </p>
            );
          })()}
          <div style={{ marginBottom: 8 }} />

          <label style={s.label}>CONTROL DE DUPLICADOS</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {[
              { value: "band_mode", label: "Bloquear si ya está trabajado en esa banda y ese modo", hint: "El mismo indicativo puede repetirse en otra banda o en otro modo. Es el criterio habitual." },
              { value: "band_mode_day", label: "Bloquear solo si ya está trabajado ese mismo día, en esa banda y modo", hint: "Puede repetirse en otro día del concurso — habitual en concursos de varios días." },
              { value: "mode", label: "Bloquear si ya está trabajado en ese modo, en cualquier banda", hint: "Útil si el concurso puntúa \"una vez por modo\" sin importar la banda." },
              { value: "activity", label: "Bloquear si ya está trabajado en cualquier banda/modo de la actividad", hint: "El más estricto: cada indicativo cuenta solo una vez en toda la actividad." },
              { value: "none", label: "Permitir duplicados (sin ninguna validación)", hint: "" },
            ].map((opt) => (
              <label key={opt.value} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="duplicateScope"
                  checked={form.duplicateScope === opt.value}
                  onChange={() => setForm({ ...form, duplicateScope: opt.value })}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontSize: 13.5, color: colors.text, display: "block" }}>{opt.label}</span>
                  {opt.hint && <span style={{ fontSize: 11.5, color: colors.textFaint }}>{opt.hint}</span>}
                </span>
              </label>
            ))}
          </div>

          <label style={s.label}>CAMPOS DEL QSO</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: colors.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.requireRst} onChange={(e) => setForm({ ...form, requireRst: e.target.checked })} />
              RS(T)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: colors.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.requireExchange} onChange={(e) => setForm({ ...form, requireExchange: e.target.checked })} />
              Exchange / N.º de serie
            </label>
            {form.requireExchange && (
              <div style={{ paddingLeft: 26, marginTop: -2 }}>
                <p style={{ fontSize: 11.5, color: colors.textFaint, marginBottom: 6 }}>
                  En fonía y CW se rellena solo con un número progresivo (001, 002...). En digitales no aplica.
                </p>
                <label style={s.label}>ÁMBITO DEL NÚMERO PROGRESIVO</label>
                <select
                  value={form.exchangeScope}
                  onChange={(e) => setForm({ ...form, exchangeScope: e.target.value })}
                  style={{ ...s.input, background: colors.panel, maxWidth: 300 }}
                >
                  <option value="general" style={{ background: colors.panelRaised }}>General — una cuenta para toda la actividad</option>
                  <option value="band" style={{ background: colors.panelRaised }}>Por banda</option>
                  <option value="mode" style={{ background: colors.panelRaised }}>Por modo</option>
                  <option value="band_mode" style={{ background: colors.panelRaised }}>Por banda y modo combinados</option>
                </select>
              </div>
            )}

            <label style={s.label}>RETENCIÓN DEL CHAT DE LA ACTIVIDAD (DÍAS)</label>
            <input
              type="number" min="1" style={{ ...s.input, maxWidth: 140, marginBottom: 16 }}
              value={form.chatRetentionDays}
              onChange={(e) => setForm({ ...form, chatRetentionDays: e.target.value })}
            />

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: colors.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.requireLocator} onChange={(e) => setForm({ ...form, requireLocator: e.target.checked })} />
              Locator / QTH
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: colors.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.clusterPublishEnabled} onChange={(e) => setForm({ ...form, clusterPublishEnabled: e.target.checked })} />
              Publicación en DX Cluster habilitada
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: form.isOta ? colors.amber : colors.text, cursor: formState === "new" ? "pointer" : "default" }}>
              <input
                type="checkbox" checked={form.isOta} disabled={formState !== "new"}
                onChange={(e) => setForm({ ...form, isOta: e.target.checked })}
              />
              Actividad OTA (SOTA / POTA / IOTA / DME...) — interfaz simplificada de móvil
            </label>
            {formState !== "new" && (
              <p style={{ fontSize: 11.5, color: colors.textFaint, margin: "-6px 0 0 24px" }}>
                Fijo desde la creación — no se puede cambiar después.
              </p>
            )}
          </div>

          <label style={s.label}>INFORME POR EMAIL AL ORGANIZADOR</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {[
              { value: "none", label: "Ninguno" },
              { value: "daily", label: "Diario, a las 00:00 UTC (mientras esté en curso)" },
              { value: "on_finish", label: "Solo al finalizar la actividad" },
            ].map((opt) => (
              <label key={opt.value} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="statsReportMode"
                  checked={form.statsReportMode === opt.value}
                  onChange={() => setForm({ ...form, statsReportMode: opt.value })}
                />
                <span style={{ fontSize: 13.5, color: colors.text }}>{opt.label}</span>
              </label>
            ))}
          </div>

          <label style={s.label}>PUNTUACIÓN</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {[
              { value: "fixed", label: "Puntos fijos por QSO" },
              { value: "per_band", label: "Puntos distintos según la banda" },
              { value: "per_band_mode", label: "Puntos distintos según banda y modo combinados" },
            ].map((opt) => (
              <label key={opt.value} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="scoringMode"
                  checked={form.scoringMode === opt.value}
                  onChange={() => setForm({ ...form, scoringMode: opt.value })}
                />
                <span style={{ fontSize: 13.5, color: colors.text }}>{opt.label}</span>
              </label>
            ))}
          </div>

          {form.scoringMode === "fixed" && (
            <div style={{ marginBottom: 20, width: 140 }}>
              <label style={s.label}>PUNTOS POR QSO</label>
              <input
                type="number" min="0" style={s.input}
                value={form.pointsFixed}
                onChange={(e) => setForm({ ...form, pointsFixed: e.target.value })}
              />
            </div>
          )}

          {form.scoringMode === "per_band" && (
            <div style={{ marginBottom: 20 }}>
              {form.bands.length === 0 && <p style={{ fontSize: 12.5, color: colors.textFaint }}>Marca primero las bandas de esta actividad, arriba.</p>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {form.bands.map((bandId) => (
                  <div key={bandId} style={{ width: 90 }}>
                    <label style={s.label}>{bandId.toUpperCase()}</label>
                    <input
                      type="number" min="0" style={s.input}
                      value={form.pointsByBand[bandId] ?? ""}
                      placeholder={String(form.pointsFixed)}
                      onChange={(e) => setForm({ ...form, pointsByBand: { ...form.pointsByBand, [bandId]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.scoringMode === "per_band_mode" && (
            <div style={{ marginBottom: 20 }}>
              {(form.bands.length === 0 || form.modes.length === 0) && <p style={{ fontSize: 12.5, color: colors.textFaint }}>Marca primero las bandas y modos de esta actividad, arriba.</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {form.bands.map((bandId) => (
                  <div key={bandId}>
                    <label style={s.label}>{bandId.toUpperCase()}</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {form.modes.map((modeId) => {
                        const key = `${bandId}|${modeId}`;
                        return (
                          <div key={key} style={{ width: 80 }}>
                            <span style={{ fontSize: 10.5, color: colors.textFaint }}>{modeId}</span>
                            <input
                              type="number" min="0" style={s.input}
                              value={form.pointsByBandMode[key] ?? ""}
                              placeholder={String(form.pointsFixed)}
                              onChange={(e) => setForm({ ...form, pointsByBandMode: { ...form.pointsByBandMode, [key]: e.target.value } })}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: colors.text, cursor: "pointer", marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={form.qslEnabled}
              onChange={(e) => setForm({
                ...form,
                qslEnabled: e.target.checked,
                // 30 días por defecto al activarlo, si no había nada puesto ya
                qslDaysValid: e.target.checked && form.qslDaysValid === "" ? 30 : form.qslDaysValid,
              })}
            />
            Habilitar QSL descargable por los participantes
          </label>

          {form.qslEnabled && (
            <div style={{ marginBottom: 20, paddingLeft: 4 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {[
                  { value: "per_contact", label: "Una QSL por cada contacto" },
                  { value: "summary", label: "Un diploma resumen único" },
                ].map((opt) => (
                  <label key={opt.value} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="qslMode"
                      checked={form.qslMode === opt.value}
                      onChange={() => setForm({ ...form, qslMode: opt.value })}
                    />
                    <span style={{ fontSize: 13.5, color: colors.text }}>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div style={{ width: 220 }}>
                <label style={s.label}>DÍAS DE VALIDEZ TRAS FINALIZAR (MÁX. 60)</label>
                <input
                  type="number" min="0" max="60" style={s.input} placeholder="Sin límite"
                  value={form.qslDaysValid}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({ ...form, qslDaysValid: v === "" ? "" : Math.min(60, Math.max(0, Number(v))) });
                  }}
                />
              </div>
              <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: 8 }}>
                La plantilla y la posición de los campos se configuran después de guardar, desde "Editar plantilla QSL" en el listado.
              </p>
            </div>
          )}

          <button type="submit" style={s.btnPrimary}>{formState === "new" ? "Guardar actividad" : "Guardar cambios"}</button>
        </form>
      )}

      {error && <div style={s.errorBox}>{error}</div>}
      {reportMsg && <div style={{ color: colors.green, fontSize: 13, marginBottom: 16 }}>✓ {reportMsg}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {activities?.map((a) => {
          const isOpen = expanded === a.id;
          const assignedIds = new Set((operators[a.id] || []).map((o) => o.id));
          const availableMembers = (members || []).filter((m) => !assignedIds.has(m.id));

          return (
            <div key={a.id} style={s.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {a.logo_url && (
                    <img src={a.logo_url} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", border: `1px solid ${colors.lineStrong}` }} />
                  )}
                  <div>
                    <div style={{ fontSize: 15.5, fontWeight: 700, color: colors.text }}>
                      {a.name}
                      {a.is_ota && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: colors.amber, border: `1px solid ${colors.amber}`, borderRadius: 4, padding: "1px 6px", verticalAlign: "middle" }}>
                          OTA
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: colors.textFaint, marginTop: 3 }}>
                      {a.callsign ? `${a.callsign} · ` : ""}{a.status}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => toggleExpand(a.id)} style={s.btnGhost}>
                    {isOpen ? "Ocultar operadores" : "Operadores →"}
                  </button>
                  <button onClick={() => openEdit(a)} style={s.btnGhost}>Editar</button>
                  <button
                    onClick={() => api.downloadExport(a.id, "adif", "all", a.name.replace(/\s+/g, "_")).catch((e) => setError(e.message))}
                    style={s.btnGhost}
                  >
                    Descargar log
                  </button>
                  <button
                    onClick={async () => {
                      setError(null);
                      try {
                        await api.sendReportNow(a.id);
                        setReportMsg(`Informe de "${a.name}" enviado.`);
                        setTimeout(() => setReportMsg(null), 3000);
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                    style={s.btnGhost}
                  >
                    Enviar informe ahora
                  </button>
                  {a.qsl_enabled && (
                    <Link to={`/organizador/${a.id}/qsl-plantilla`} style={{ ...s.btnGhost, textDecoration: "none" }}>
                      Editar plantilla QSL
                    </Link>
                  )}
                  <button onClick={() => setQrzWidgetActivity(a)} style={s.btnGhost}>
                    Código para tu QRZ
                  </button>
                  {a.status !== "en_curso" && a.status !== "finalizada" && (
                    <button onClick={() => api.updateActivity(a.id, { status: "en_curso" }).then(load)} style={s.btnPrimary}>
                      Activar concurso
                    </button>
                  )}
                  {a.status === "en_curso" && (
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Marcar "${a.name}" como finalizada?`)) {
                          api.updateActivity(a.id, { status: "finalizada" }).then(load);
                        }
                      }}
                      style={s.btnGhost}
                    >
                      Finalizar actividad
                    </button>
                  )}
                  <button
                    onClick={() => remove(a)}
                    style={{ background: "transparent", border: "1px solid #7a2c2c", color: "#ffb4b4", borderRadius: 6, fontWeight: 700, fontSize: 13, padding: "9px 14px", cursor: "pointer" }}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.line}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: colors.textFaint, marginBottom: 10 }}>
                    OPERADORES ASIGNADOS
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                    {(operators[a.id] || []).map((op) => (
                      <div key={op.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "7px 12px" }}>
                        <span style={{ fontFamily: fontMono, fontSize: 13 }}>{op.callsign} — {op.name}</span>
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Quitar a ${op.callsign} de esta actividad?`)) removeOp(a.id, op.id);
                          }}
                          style={{ background: "transparent", border: `1px solid ${colors.lineStrong}`, color: colors.textDim, borderRadius: 5, fontSize: 11, fontWeight: 600, padding: "4px 8px", cursor: "pointer" }}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                    {(operators[a.id] || []).length === 0 && (
                      <p style={{ fontSize: 12.5, color: colors.textFaint, margin: 0 }}>Ningún operador asignado todavía.</p>
                    )}
                  </div>

                  {availableMembers.length > 0 ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: colors.textFaint, marginBottom: 8 }}>
                        AÑADIR DESDE LOS MIEMBROS DEL CLUB
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {availableMembers.map((m) => (
                          <button key={m.id} onClick={() => addOp(a.id, m.id)} style={{ ...miniAddBtn }}>
                            + {m.callsign}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: 12, color: colors.textFaint, margin: 0 }}>
                      No hay más miembros del club sin asignar. Añade primero operadores en la pestaña "Miembros del club".
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {activities?.length === 0 && <p style={{ color: colors.textFaint, fontSize: 13.5 }}>Todavía no hay actividades en este club.</p>}
      </div>

      {qrzWidgetActivity && (
        <QrzWidgetModal activity={qrzWidgetActivity} onClose={() => setQrzWidgetActivity(null)} />
      )}
    </div>
  );
}

function QrzWidgetModal({ activity, onClose }) {
  // "v3" como parte de la RUTA (no "?v=3") — un parámetro de query no
  // bastó, alguna caché intermedia lo estaba ignorando y sirviendo la
  // copia vieja de todas formas. Como parte de la ruta, ninguna caché
  // puede confundirlo con la anterior.
  const embedUrl = `${window.location.origin}/embed/actividad/${activity.id}/v3`;
  const iframeCode = `<iframe src="${embedUrl}" width="680" height="900" frameborder="0" scrolling="yes"></iframe>`;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(iframeCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: colors.panel, border: `1px solid ${colors.lineStrong}`, borderRadius: 12, maxWidth: 760, width: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <h2 style={{ ...s.h2, margin: 0 }}>Código para tu QRZ — {activity.name}</h2>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: colors.textFaint, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: colors.textDim, marginBottom: 16 }}>
          Pégalo en tu biografía de QRZ (edítala, pulsa "Source" antes de pegar) — en el idioma de quien lo mire,
          sin que tengas que volver a tocarlo. Los datos se ven siempre frescos cada vez que alguien carga o recarga
          la página (QRZ bloquea que se actualice solo mientras está a la vista, es una restricción de seguridad
          del propio navegador). Sigue funcionando cuando el concurso termine, mostrando el resumen final y el
          botón de QSL.
        </p>

        <label style={s.label}>CÓDIGO A COPIAR</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <textarea
            readOnly value={iframeCode} rows={2}
            style={{ ...s.input, flex: 1, fontFamily: fontMono, fontSize: 12, resize: "none" }}
            onClick={(e) => e.target.select()}
          />
          <button onClick={copy} style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>
            {copied ? "✓ Copiado" : "Copiar"}
          </button>
        </div>

        <label style={s.label}>VISTA PREVIA</label>
        <div style={{ border: `1px solid ${colors.lineStrong}`, borderRadius: 8, overflow: "hidden", background: "#f4f4f2" }}>
          <iframe src={embedUrl} width="100%" height="700" frameBorder="0" title="Vista previa del widget" />
        </div>
      </div>
    </div>
  );
}

const miniAddBtn = {
  fontFamily: fontMono, fontSize: 12, fontWeight: 700, color: colors.green, background: colors.greenBg,
  border: "1px solid #1c4a30", borderRadius: 5, padding: "5px 10px", cursor: "pointer",
};

function MembersTab({ clubId }) {
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState("operador");

  const load = () => api.listClubMembers(clubId).then(setMembers).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [clubId]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.searchUsers(query).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const add = async (userId) => {
    await api.addClubMember(clubId, userId, roleToAdd);
    setQuery(""); setResults([]);
    load();
  };
  const remove = async (userId) => {
    await api.removeClubMember(clubId, userId);
    load();
  };
  const setRole = async (userId, role) => {
    await api.addClubMember(clubId, userId, role); // el backend hace upsert: si ya existe, actualiza el rol
    load();
  };

  return (
    <div>
      <p style={{ color: colors.textDim, fontSize: 14, marginBottom: 14 }}>
        Operadores que pertenecen a este club y pueden asignarse a actividades. Los que tengan rol
        "Admin de club" pasan a ser organizadores: pueden crear actividades y gestionar este club.
      </p>

      <div style={{ ...s.card, marginBottom: 16, position: "relative" }}>
        <label style={s.label}>AÑADIR MIEMBRO EXISTENTE (POR INDICATIVO O NOMBRE)</label>
        <input style={s.input} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar…" />

        <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: colors.textDim, cursor: "pointer" }}>
            <input type="radio" name="roleToAdd" checked={roleToAdd === "operador"} onChange={() => setRoleToAdd("operador")} />
            Operador
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: colors.textDim, cursor: "pointer" }}>
            <input type="radio" name="roleToAdd" checked={roleToAdd === "admin"} onChange={() => setRoleToAdd("admin")} />
            Admin de club (organizador)
          </label>
        </div>

        {results.length > 0 && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            {results.map((u) => (
              <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "8px 12px" }}>
                <span style={{ fontFamily: fontMono, fontSize: 13 }}>{u.callsign} — {u.name}</span>
                <button onClick={() => add(u.id)} style={{ ...s.btnPrimary, padding: "5px 10px", fontSize: 12 }}>Añadir como {roleToAdd === "admin" ? "admin" : "operador"}</button>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: 8, marginBottom: 0 }}>
          Solo puedes añadir usuarios que ya existen en la plataforma. Un mismo indicativo puede pertenecer a varios clubs a la vez.
        </p>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={{ border: `1px solid ${colors.line}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Indicativo</Th><Th>Nombre</Th><Th>Rol</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {members?.map((m) => (
              <tr key={m.id}>
                <Td style={{ fontFamily: fontMono, fontWeight: 700 }}>{m.callsign}</Td>
                <Td>{m.name}</Td>
                <Td>
                  <span style={{
                    fontSize: 11.5, fontWeight: 700, borderRadius: 5, padding: "3px 9px",
                    color: m.role === "admin" ? colors.amber : colors.textDim,
                    background: m.role === "admin" ? "#2a2013" : colors.panelRaised,
                    border: `1px solid ${m.role === "admin" ? "#5a4419" : colors.lineStrong}`,
                  }}>
                    {m.role === "admin" ? "Admin de club" : "Operador"}
                  </span>
                </Td>
                <Td style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {m.role === "admin" ? (
                      <button onClick={() => setRole(m.id, "operador")} style={smallGhostBtn}>Quitar admin</button>
                    ) : (
                      <button onClick={() => setRole(m.id, "admin")} style={smallGhostBtn}>Hacer admin</button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Quitar a ${m.callsign} de este club? Dejará de estar asignado a sus actividades.`)) remove(m.id);
                      }}
                      style={smallGhostBtn}
                    >
                      Quitar del club
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const smallGhostBtn = { background: "transparent", border: "1px solid #3f4c58", color: "#dce4ec", borderRadius: 5, fontSize: 11.5, fontWeight: 600, padding: "5px 9px", cursor: "pointer", whiteSpace: "nowrap" };

function Toggle({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      fontFamily: fontMono, fontSize: 12.5, fontWeight: 700, borderRadius: 5, padding: "5px 10px", cursor: "pointer",
      border: `1.5px solid ${active ? colors.green : colors.lineStrong}`,
      color: active ? colors.green : colors.textFaint,
      background: active ? colors.greenBg : "transparent",
    }}>
      {children}
    </button>
  );
}
function Th({ children }) {
  return <th style={{ textAlign: "left", fontSize: 11, color: colors.textFaint, fontWeight: 700, padding: "10px 16px", background: colors.panelRaised, borderBottom: `1px solid ${colors.lineStrong}` }}>{children}</th>;
}
function Td({ children, style }) {
  return <td style={{ fontSize: 13.5, padding: "10px 16px", borderBottom: "1px solid #16202a", ...style }}>{children}</td>;
}
